import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ContentCopy, Download, QrCode2, Refresh } from '@mui/icons-material';

import type { ConnectorCapabilityVerdict } from '../connectorCapabilityResolution';
import { TicketsResult, TravelTicket } from '../queries';
import {
  associatedTicketDirection,
  copyTicketUrl,
  effectiveTicketResult,
  TicketCopyState,
  ticketCopyStatus,
  ticketFetchSurfaces,
  ticketLabelKey,
  ticketRenderRows,
  useTicketExpiryTimer,
} from '../ticket-utils';

interface Props {
  tickets: TicketsResult;
  /**
   * Qué se reserva. El mismo `kind` significa cosas distintas según el
   * producto: en eSIMs `file` es la IMAGEN del QR y `selfservice` un universal
   * link de Apple, no dos documentos que se bajan.
   */
  vertical: string;
  isRefreshing: boolean;
  /** El cliente está reintentando automáticamente una respuesta pendiente. */
  isPolling: boolean;
  /** Se agotó la ventana automática; el asesor puede iniciar otra manualmente. */
  pollingExhausted: boolean;
  /**
   * Veredicto del conector sobre `tickets`. El botón "Actualizar" de las URLs
   * vencidas es OTRA llamada al proveedor: solo existe con `supported`.
   */
  fetchVerdict: ConnectorCapabilityVerdict;
  onRefresh: () => void;
}

function formatTime(value: string): string {
  const at = new Date(value);
  return Number.isNaN(at.getTime())
    ? value
    : at.toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Documentos del viaje. Varios operadores FIRMAN las URLs con vencimiento (el
 * de Stena Line dura horas): pasado ese momento solo se ocultan las URLs de
 * red vencidas. Referencias y QR embebidos siguen visibles, y —si el conector
 * expone tickets por API— se ofrece volver a pedir links firmados en vez de
 * mandar al pasajero a una página caída. Si no los expone, las URLs vencidas
 * se ocultan igual, pero no hay botón: sería la misma llamada al hub que ya
 * sabemos que devuelve un 400.
 */
export function BookingTickets({
  tickets,
  vertical,
  isRefreshing,
  isPolling,
  pollingExhausted,
  fetchVerdict,
  onRefresh,
}: Props) {
  const { t } = useTranslation();
  useTicketExpiryTimer(tickets.expiresAt);
  const effective = effectiveTicketResult({
    ready: tickets.ready,
    tickets: tickets.tickets,
    expiresAt: tickets.expiresAt,
  });
  const surfaces = ticketFetchSurfaces({ verdict: fetchVerdict, effective });
  const showExpiryCaption = Boolean(
    tickets.expiresAt &&
      effective.hasNetworkTicketUrls &&
      !effective.networkTicketUrlsExpired,
  );

  if (!tickets.ready) {
    const retryAction = surfaces.canFetch && pollingExhausted ? (
      <Button
        size="small"
        startIcon={<Refresh />}
        disabled={isRefreshing}
        onClick={onRefresh}
      >
        {t('travelServices.booking.ticketsRefresh')}
      </Button>
    ) : undefined;
    return (
      <Alert severity="info" action={retryAction}>
        {t(
          pollingExhausted
            ? 'travelServices.booking.ticketsPollingStopped'
            : isPolling
              ? 'travelServices.booking.ticketsPolling'
              : 'travelServices.booking.ticketsNotReady',
        )}
      </Alert>
    );
  }

  const refreshAction = surfaces.canRefreshExpired ? (
    <Button
      size="small"
      startIcon={<Refresh />}
      disabled={isRefreshing}
      onClick={onRefresh}
    >
      {t('travelServices.booking.ticketsRefresh')}
    </Button>
  ) : undefined;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('travelServices.booking.tickets')}
      </Typography>

      {!effective.ready ? (
        <Alert severity="warning" action={refreshAction}>
          {effective.networkTicketUrlsExpired
            ? t('travelServices.booking.ticketsExpired')
            : t('travelServices.booking.ticketsNotReady')}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {effective.networkTicketUrlsExpired && (
            <Alert severity="warning" action={refreshAction}>
              {t('travelServices.booking.ticketsExpired')}
            </Alert>
          )}
          {ticketRenderRows(surfaces.visibleTickets).map(({ key, ticket }) => (
            <TicketRow key={key} ticket={ticket} vertical={vertical} />
          ))}
          {showExpiryCaption && tickets.expiresAt && (
            <Typography variant="caption" color="text.secondary">
              {t('travelServices.booking.ticketsExpireAt', {
                date: formatTime(tickets.expiresAt),
              })}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

function TicketRow({
  ticket,
  vertical,
}: {
  ticket: TravelTicket;
  vertical: string;
}) {
  const { t } = useTranslation();
  /**
   * Estado atado a la URL: los links firmados rotan al refrescar. Una imagen
   * que falló o un "¡Copiado!" valen para el link con el que pasaron; con el
   * link nuevo se vuelve a empezar (imagen se reintenta, copiado vuelve a
   * idle). La key de la fila incluye esa identidad, no su posición.
   */
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const imageFailed = Boolean(ticket.url) && failedImageUrl === ticket.url;
  const labelKey = ticketLabelKey(ticket, vertical);
  const documentLabel = labelKey ? t(labelKey) : (ticket.description as string);
  const direction = associatedTicketDirection(ticket);
  let label = documentLabel;
  if (direction) {
    label = t(`travelServices.booking.ticketDirections.${direction}`, {
      document: documentLabel,
    });
  }
  const installation = vertical === 'esims' ? ticket.esimInstallation : undefined;

  // Código alfanumérico: se activa en la app del proveedor, no hay descarga.
  if (ticket.kind === 'code') {
    return (
      <Box>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="h6" fontFamily="monospace">
          {ticket.reference}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('travelServices.booking.ticketCodeHint')}
        </Typography>
        {installation && <EsimInstallationFields installation={installation} />}
      </Box>
    );
  }

  // Código de barras: es una imagen para escanear en el embarque.
  if (ticket.kind === 'barcode' && ticket.url) {
    return (
      <Box>
        <Typography variant="body2" gutterBottom>
          {label}
        </Typography>
        {imageFailed ? (
          <Link href={ticket.url} target="_blank" rel="noopener noreferrer" variant="body2">
            <QrCode2 fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            {t('travelServices.booking.ticketOpen')}
          </Link>
        ) : (
          <Box
            component="img"
            src={ticket.url}
            alt={label}
            onError={() => setFailedImageUrl(ticket.url ?? null)}
            sx={{ maxWidth: 320, maxHeight: 160, objectFit: 'contain' }}
          />
        )}
        {installation && <EsimInstallationFields installation={installation} />}
      </Box>
    );
  }

  return (
    <Box>
      {ticket.url ? (
        <Link href={ticket.url} target="_blank" rel="noopener noreferrer" variant="body2">
          <Download fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          {label}
        </Link>
      ) : (
        <Typography variant="body2">
          {label}
          {ticket.reference ? `: ${ticket.reference}` : ''}
        </Typography>
      )}
      {installation && <EsimInstallationFields installation={installation} />}
    </Box>
  );
}

function EsimInstallationValue({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState<TicketCopyState>(null);
  const status = ticketCopyStatus(copyState, value);
  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        label={label}
        value={value}
        error={status === 'failed'}
        helperText={status === 'failed' ? t('travelServices.booking.esimCopyFailed') : undefined}
        onFocus={(event) => event.target.select()}
        inputProps={{ readOnly: true, style: { fontFamily: 'monospace' } }}
      />
      <Button
        size="small"
        startIcon={<ContentCopy />}
        onClick={async () => setCopyState(await copyTicketUrl(value))}
      >
        {t(status === 'copied' ? 'travelServices.booking.esimQrCopied' : 'travelServices.booking.esimCopyValue')}
      </Button>
    </Box>
  );
}

function EsimInstallationFields({ installation }: { installation: NonNullable<TravelTicket['esimInstallation']> }) {
  const { t } = useTranslation();
  const fields = [
    ['esimIccid', installation.iccid],
    ['esimSmdpAddress', installation.smdpAddress],
    ['esimActivationCode', installation.activationCode],
    ['esimQrCodeData', installation.qrCodeData],
  ] as const;
  return (
    <Stack spacing={1} mt={1}>
      <Typography variant="subtitle2">{t('travelServices.booking.esimInstallation')}</Typography>
      {fields.map(([key, value]) =>
        value ? (
          <EsimInstallationValue
            key={key}
            label={t(`travelServices.booking.${key}`)}
            value={value}
          />
        ) : null)}
    </Stack>
  );
}
